import { useState, useEffect } from 'react';

export interface NotificationItem {
  id: string;
  type: 'success' | 'info' | 'warning' | 'action';
  title: string;
  message: string;
  time: string;
  icon: string;
  read: boolean;
  link?: string;
}

const STORAGE_KEY = 'connectx_notifications';
const SYNC_EVENT = 'connectx_notifications_sync';

const INITIAL_NOTIFICATIONS: NotificationItem[] = [
  {
    id: 'notif-1',
    type: 'action',
    title: 'New Referral Request',
    message: 'A candidate has requested a referral for Senior Software Engineer at Google.',
    time: '30 mins ago',
    icon: '💼',
    read: false,
    link: '/requests'
  },
  {
    id: 'notif-2',
    type: 'success',
    title: 'Profile Verification Approved',
    message: 'Your profile has been verified successfully by the admin team.',
    time: '2 hours ago',
    icon: '✅',
    read: false,
    link: '/profile'
  },
  {
    id: 'notif-3',
    type: 'info',
    title: 'Platform Maintenance',
    message: 'Scheduled database optimization will happen on Sunday from 2 AM to 4 AM EST.',
    time: '1 day ago',
    icon: '🔧',
    read: true
  },
  {
    id: 'notif-4',
    type: 'success',
    title: 'New Feature: Enhanced Profile',
    message: 'Update your profile with new fields for better candidate matching.',
    time: '3 days ago',
    icon: '✨',
    read: true,
    link: '/profile'
  }
];

export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Load initial notifications
  useEffect(() => {
    const loadNotifications = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          setNotifications(JSON.parse(stored));
        } catch (e) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_NOTIFICATIONS));
          setNotifications(INITIAL_NOTIFICATIONS);
        }
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_NOTIFICATIONS));
        setNotifications(INITIAL_NOTIFICATIONS);
      }
    };

    loadNotifications();

    // Listen for sync event from other components/tabs
    const handleSync = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          setNotifications(JSON.parse(stored));
        } catch (e) {
          // ignore
        }
      }
    };

    window.addEventListener(SYNC_EVENT, handleSync);
    window.addEventListener('storage', handleSync); // sync across tabs

    return () => {
      window.removeEventListener(SYNC_EVENT, handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const saveNotifications = (newNotifs: NotificationItem[]) => {
    setNotifications(newNotifs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newNotifs));
    // Dispatch custom event to sync with other instances of hook
    window.dispatchEvent(new Event(SYNC_EVENT));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    const updated = notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    );
    saveNotifications(updated);
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveNotifications(updated);
  };

  const clearAll = () => {
    saveNotifications([]);
  };

  const addNotification = (item: Omit<NotificationItem, 'id' | 'read' | 'time'>) => {
    const newNotif: NotificationItem = {
      ...item,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      read: false,
      time: 'Just now'
    };
    saveNotifications([newNotif, ...notifications]);
  };

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    addNotification
  };
}
